/**
 * Hook que mantiene el conteo de mensajes pendientes para el badge del nav.
 * - Carga inicial + polling cada 30s via GET /tenants/:slug/agent-messages/stats
 * - NOTE: SSE descartado — EventSource no soporta headers de auth y el endpoint
 *   /events/stream requiere Authorization header (no acepta ?token= en query).
 */

import { useEffect, useRef, useState } from 'react';
import { apiExt } from '@/lib/api';

const POLL_INTERVAL_MS = 30_000;

export function useMessageBadge(tenantSlug: string | null) {
  const [pendingCount, setPendingCount] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!tenantSlug) return;
    // Skip polling in E2E mode — prevents CDP network idle detection issues
    if (typeof window !== 'undefined' && localStorage.getItem('e2e') === 'true') return;

    const load = () => {
      apiExt.agentMessages
        .stats(tenantSlug)
        .then((s) => setPendingCount(s.total_pending))
        .catch(() => {/* non-critical */});
    };

    load();
    intervalRef.current = setInterval(load, POLL_INTERVAL_MS);

    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [tenantSlug]);

  // Expose a decrement to call when user reads a message
  const decrementPending = () =>
    setPendingCount((prev) => Math.max(0, prev - 1));

  return { pendingCount, decrementPending };
}
